import React, { useEffect, useMemo, useState } from 'react';
import { Card, Spin, Alert, Typography } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import type { RootState } from '@/store';
import {
  getDashboardMetric11,
  type DashboardMetric11GapWaterfall,
} from '@/services/dashboardService';

const { Text } = Typography;

type WaterfallRow = {
  key: 'START' | 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'END';
  label: string;
  base: number; // hidden base (running total before step)
  delta: number; // visible floating bar height (the movement)
  total: number; // for start/end bars
  isTotal: boolean;
};

const COLORS = {
  start: '#6B7280', // grey
  increase: '#EF4444', // red (net increase)
  decrease: '#00A37A', // green (net decrease)
  end: '#2563EB', // blue
};

export type EX1NewGapsVsArchivedFiscalYearWaterfallChartProps = {
  height?: number;
  title?: string;
};

const Metric11Tooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const row: WaterfallRow | undefined = payload?.[0]?.payload;
  if (!row) return null;

  const displayValue = row.isTotal ? row.total : row.delta;

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 8,
        padding: 10,
        minWidth: 220,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{row.label}</div>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}
      >
        <span>{row.isTotal ? 'Count' : 'Net movement'}</span>
        <span style={{ fontWeight: 700 }}>{displayValue}</span>
      </div>
      {!row.isTotal ? (
        <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.6)' }}>
          Click to open Exhibit 6 for this quarter
        </div>
      ) : null}
    </div>
  );
};

export const EX1NewGapsVsArchivedFiscalYearWaterfallChart: React.FC<
  EX1NewGapsVsArchivedFiscalYearWaterfallChartProps
> = ({
  height = 360,
  title = 'Quarterly GAPs Continuity',
}) => {
  const navigate = useNavigate();

  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [raw, setRaw] = useState<DashboardMetric11GapWaterfall | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRaw(null);
        setErrorMessage(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric11(annualReportId);
        if (!mounted) return;
        setRaw(data);
      } catch (err: any) {
        if (!mounted) return;
        setRaw(null);
        setErrorMessage(
          err?.message ||
            err?.Message ||
            'Failed to load Metric 11 (Waterfall).',
        );
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [annualReportId]);

  const rows: WaterfallRow[] = useMemo(() => {
    if (!raw) return [];

    const start = raw.yearStartCount || 0;
    const q1 = raw.quarterOneCount || 0;
    const q2 = raw.quarterTwoCount || 0;
    const q3 = raw.quarterThreeCount || 0;
    const q4 = raw.quarterFourCount || 0;
    const end = raw.currentCount || 0;

    // Running totals for floating bars
    const baseQ1 = start;
    const afterQ1 = start + q1;

    const baseQ2 = afterQ1;
    const afterQ2 = afterQ1 + q2;

    const baseQ3 = afterQ2;
    const afterQ3 = afterQ2 + q3;

    const baseQ4 = afterQ3;
    // afterQ4 could be computed but end is authoritative from API
    // const afterQ4 = afterQ3 + q4;

    return [
      {
        key: 'START',
        label: 'Start of FY',
        base: 0,
        delta: 0,
        total: start,
        isTotal: true,
      },
      {
        key: 'Q1',
        label: 'Q1',
        base: baseQ1,
        delta: q1,
        total: 0,
        isTotal: false,
      },
      {
        key: 'Q2',
        label: 'Q2',
        base: baseQ2,
        delta: q2,
        total: 0,
        isTotal: false,
      },
      {
        key: 'Q3',
        label: 'Q3',
        base: baseQ3,
        delta: q3,
        total: 0,
        isTotal: false,
      },
      {
        key: 'Q4',
        label: 'Q4',
        base: baseQ4,
        delta: q4,
        total: 0,
        isTotal: false,
      },
      {
        key: 'END',
        label: 'Current Total',
        base: 0,
        delta: 0,
        total: end,
        isTotal: true,
      },
    ];
  }, [raw]);

  const isEmpty = !loading && !errorMessage && rows.length === 0;

  const statusMap: Record<string, string[]> = {
      START: ['Open', 'Archived'],
      Q1: ['Open', 'Archived'],
      Q2: ['Open', 'Archived'],
      Q3: ['Open', 'Archived'],
      Q4: ['Open', 'Archived'],
      END: ['Open'],
    };

  // Interaction routing (you’ll align query params to your Exhibit 6 contract)
  const quarterToNumber: Record<string, string> = { START:'0', Q1: '1', Q2: '2', Q3: '3', Q4: '4' };

  const goToExhibit6ForQuarter = (quarter: 'START' | 'Q1' | 'Q2' | 'Q3' | 'Q4') => {
    const q = quarterToNumber[quarter];
    navigate('/gaps', {
      state: { gapDateQuarter: [q], gapClosureDateQuarter: [q], status: statusMap[quarter] },
    });
  };

  const goToExhibit6NoQuarter = (quarter: 'END') => {
    navigate('/gaps', {
      state: { status: statusMap[quarter] },
    });
  };

  const barColorForRow = (r: WaterfallRow) => {
    if (r.key === 'START') return COLORS.start;
    if (r.key === 'END') return COLORS.end;
    return r.delta >= 0 ? COLORS.increase : COLORS.decrease;
  };

  return (
    <Card title={title}>
      <div style={{ height }}>
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin />
          </div>
        ) : errorMessage ? (
          <Alert
            type='error'
            message='Dashboard Metric 11 Error'
            description={errorMessage}
            showIcon
          />
        ) : isEmpty ? (
          <Alert
            type='info'
            message='No data'
            description='No waterfall data returned for this report.'
            showIcon
          />
        ) : (
          <ResponsiveContainer width='100%' height='95%'>
            <BarChart
              data={rows}
              margin={{ top: 30, right: 24, bottom: 24, left: 16 }}
            >
              <XAxis dataKey='label' />
              <YAxis
                label={{
                  value: '# of Open Gaps',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -6,
                }}
              />
              <Tooltip content={<Metric11Tooltip />} />

              {/* Hidden base bar to lift the floating delta bars */}
              <Bar dataKey='base' stackId='wf' fill='rgba(0,0,0,0)' />

              {/* Visible bars: start/end use "total", quarters use "delta" */}
              <Bar
                dataKey={(r: any) => (r.isTotal ? r.total : r.delta)}
                stackId='wf'
                isAnimationActive={false}
                onClick={(_, idx) => {
                  const r = rows[idx];
                  if (
                    r?.key === 'Q1' ||
                    r?.key === 'Q2' ||
                    r?.key === 'Q3' ||
                    r?.key === 'Q4' ||
                    r?.key === 'START'
                  ) {
                    goToExhibit6ForQuarter(r.key);
                  }
                  else {
                    goToExhibit6NoQuarter(r.key);
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                {rows.map((r, idx) => (
                  <>
                  <LabelList style={{fill: "black"}} dataKey={(r: any) => (r.isTotal ? r.total : r.delta)} position="top" />
                  <Cell key={`cell-${idx}`} fill={barColorForRow(r)} />
                  </>
                ))}
              </Bar>
            </BarChart>

            
          </ResponsiveContainer>
        )}
        <div style={{ marginTop: 6, textAlign: 'center' }}>
              <Text type='secondary'>
                Quarter Value = # of Open GAPs - # of Closed/Archived GAPs
              </Text>
            </div>
      </div>
    </Card>
  );
};

export default EX1NewGapsVsArchivedFiscalYearWaterfallChart;
